import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import Addon, { IAddonItem } from './Addon'
import AddonSkeleton from './AddonSkeleton'
import FreeBadge from './FreeBadge'
import { IOption, IProduct, StockStatus } from '../context/ProductDataStore'
import { StockCheckCalculatedStock } from '@/services/api'
import { ScrollContainerContext } from '@/components/ScrollToTop'
import { getLegacyThumbnailPath, getProductThumbnailUrl } from '@/utils/productImages'

interface ProductAddonSelectorProps {
    isLoading: boolean
    options: IOption[]
    colourWays: IAddonItem[]
    selectedColorWay: string | null
    currencySymbol: string
    discountCode: any
    stockById?: Record<string, StockCheckCalculatedStock>
    basketRequestedCounts?: Record<string, number>
    getActiveId: (productCatalogueId: string) => string | null
    isAddonFree: (productId: string, catalogueId: string) => boolean
    handleConfiguratorChoice: (openConfigurator: boolean) => void
    setSelectedColorWay: (id: string) => void
    setIsInitialLoad: (value: boolean) => void
    setCustomColoursOpen: (value: boolean) => void
    setMoreColoursData: (data: { groupName: string; items: IAddonItem[] }) => void
    setShowMoreColoursModal: (show: boolean) => void
    handleAddonSelect: (optionId: string, value: string | null) => void
    inputChangeHandlers: Record<string, (itemId: string, inputValue: string) => void>
    primaryTextColor?: string
    secondaryTextColor?: string
    priceColor?: string
    onBlockedSelect?: (item: IAddonItem) => void
}

const ProductAddonSelector: React.FC<ProductAddonSelectorProps> = ({
    isLoading,
    options,
    colourWays,
    selectedColorWay,
    currencySymbol,
    discountCode,
    stockById,
    basketRequestedCounts = {},
    getActiveId,
    isAddonFree,
    handleConfiguratorChoice,
    setSelectedColorWay,
    setIsInitialLoad,
    setCustomColoursOpen,
    setMoreColoursData,
    setShowMoreColoursModal,
    handleAddonSelect,
    inputChangeHandlers,
    primaryTextColor,
    secondaryTextColor,
    priceColor,
    onBlockedSelect
}) => {
    const scrollContainerContext = useContext(ScrollContainerContext)
    const optionRowRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const requiredOption = useMemo(() => options.find(option => option.required) || null, [options])
    const activeColourwayPrice = useMemo(() => {
        const activeColourway = colourWays.find(colourWay => colourWay.id === selectedColorWay) || colourWays[0]
        return typeof activeColourway?.originalPrice === 'number' ? activeColourway.originalPrice : null
    }, [colourWays, selectedColorWay])
    const displayedColourWays = useMemo(() => (
        colourWays.map((colourWay) => {
            const originalPrice = typeof colourWay.originalPrice === 'number' ? colourWay.originalPrice : colourWay.price
            const comparisonPrice = activeColourwayPrice ?? originalPrice
            return {
                ...colourWay,
                price: originalPrice - comparisonPrice
            }
        })
    ), [colourWays, activeColourwayPrice])
    const orderedOptionIds = useMemo(() => {
        const ids: string[] = []
        if (requiredOption?.productCatalogueId) {
            ids.push(requiredOption.productCatalogueId)
        }
        options.forEach((option) => {
            if (!option.required && option.productCatalogueId) {
                ids.push(option.productCatalogueId)
            }
        })
        return ids
    }, [options, requiredOption])

    // const scrollToNextOptionRow = useCallback((currentOptionId: string) => {
    //     if (typeof window === 'undefined' || window.innerWidth > 768) {
    //         return
    //     }

    //     const currentIndex = orderedOptionIds.indexOf(currentOptionId)
    //     if (currentIndex === -1) {
    //         return
    //     }

    //     const nextOptionId = orderedOptionIds[currentIndex + 1]
    //     if (!nextOptionId) {
    //         return
    //     }

    //     const nextRowElement = optionRowRefs.current[nextOptionId]
    //     if (!nextRowElement) {
    //         return
    //     }

    //     const scrollContainer = scrollContainerContext.contentRef?.current
    //     if (scrollContainer) {
    //         const stickyNavOffset = 90
    //         const totalOffset = scrollContainerContext.navHeight + stickyNavOffset
    //         const containerRect = scrollContainer.getBoundingClientRect()
    //         const targetRect = nextRowElement.getBoundingClientRect()
    //         const targetPosition = targetRect.top - containerRect.top + scrollContainer.scrollTop - totalOffset

    //         scrollContainer.scrollTo({
    //             top: Math.max(0, targetPosition),
    //             behavior: 'smooth'
    //         })
    //         return
    //     }

    //     nextRowElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // }, [orderedOptionIds, scrollContainerContext])

    const queueScrollToNextRow = useCallback((_currentOptionId: string) => {
        // requestAnimationFrame(() => scrollToNextOptionRow(_currentOptionId))
    }, [])

    const resolveStockStatus = (
        calculatedStock?: StockCheckCalculatedStock,
        fallbackStatus?: StockStatus
    ): StockStatus | undefined => {
        if (calculatedStock?.stockStatus) {
            return calculatedStock.stockStatus as StockStatus
        }
        if (calculatedStock?.isPreorder === true) {
            return 'PREORDER'
        }
        if (calculatedStock?.availableForSale === false) {
            return 'OUT_OF_STOCK'
        }
        if (typeof calculatedStock?.remainingStock === 'number') {
            return calculatedStock.remainingStock > 0 ? 'LOW_STOCK' : 'OUT_OF_STOCK'
        }
        return fallbackStatus
    }

    const getSharedPoolBlock = (itemId: string): { blocked: boolean; message?: string } => {
        const stockItem = stockById?.[String(itemId)]
        const sharedPool = stockItem?.sharedPool
        if (!sharedPool || typeof sharedPool.totalAvailable !== 'number' || sharedPool.totalAvailable <= 0) {
            return { blocked: false }
        }

        const poolMembers = new Set<string>([
            String(itemId),
            ...(sharedPool.siblingProductItemIds || []).map(String)
        ])

        let poolRequestedInBasket = 0
        poolMembers.forEach((id) => {
            poolRequestedInBasket += basketRequestedCounts[String(id)] || 0
        })

        if (poolRequestedInBasket >= sharedPool.totalAvailable) {
            if (stockItem?.canPreorder === true) {
                return {
                    blocked: false,
                    message: 'Next units will be preorder.'
                }
            }
            return {
                blocked: true,
                message: 'Shared stock full.'
            }
        }

        return { blocked: false }
    }

    const getSharedPoolStatusOverride = (itemId: string): StockStatus | null => {
        const stockItem = stockById?.[String(itemId)]
        const sharedPool = stockItem?.sharedPool
        if (!sharedPool || typeof sharedPool.totalAvailable !== 'number' || sharedPool.totalAvailable <= 0) {
            return null
        }

        const poolMembers = new Set<string>([
            String(itemId),
            ...(sharedPool.siblingProductItemIds || []).map(String)
        ])

        let poolRequestedInBasket = 0
        poolMembers.forEach((id) => {
            poolRequestedInBasket += basketRequestedCounts[String(id)] || 0
        })

        if (poolRequestedInBasket >= sharedPool.totalAvailable) {
            return stockItem?.canPreorder === true ? 'PREORDER' : 'OUT_OF_STOCK'
        }

        return null
    }

    // Build-time option lists become stale as the catalogue changes. Keep the
    // static HTML neutral; the customer sees options after the live load.
    const isPrerendering = Boolean((window as Window & { __KIBU_PRERENDER_ROUTE__?: string }).__KIBU_PRERENDER_ROUTE__)
    if (isLoading || isPrerendering) {
        return (
            <>
                <AddonSkeleton />
                <AddonSkeleton />
                <AddonSkeleton />
                <AddonSkeleton />
            </>
        )
    }

    return (
        <>
            {/* Required colourway addon always first */}
            {requiredOption && (
                <div
                    ref={(element) => { optionRowRefs.current[requiredOption.productCatalogueId] = element }}
                    className="relative"
                >
                    <Addon
                        title={requiredOption?.title || "Choose Your Colourway"}
                        required
                        description={null}
                        items={displayedColourWays.map(cw => {
                            const product = requiredOption?.options.find(p => p._id === cw.id)
                            const productId = product?._id ? String(product._id) : undefined
                            const colourwayId = String(cw.id)
                            const calculatedStock = productId ? stockById?.[productId] : stockById?.[colourwayId]
                            const sharedPoolStatusOverride = getSharedPoolStatusOverride(colourwayId)
                            const stockStatus = resolveStockStatus(
                                calculatedStock,
                                product?.stockLimits?.stockStatus as StockStatus | undefined
                            )
                            const stockMessage = product?.stockLimits?.stockMessage ?? undefined
                            const remainingStock = calculatedStock?.remainingStock ?? product?.stockLimits?.remainingStock ?? null
                            const availableForSale = calculatedStock?.availableForSale ?? product?.stockLimits?.availableForSale ?? undefined
                            const sharedPool = getSharedPoolBlock(colourwayId)
                            return {
                                ...cw,
                                stockStatus: sharedPoolStatusOverride || stockStatus || 'IN_STOCK',
                                stockMessage: stockMessage,
                                remainingStock: remainingStock,
                                availableForSale: availableForSale,
                                sharedPoolBlocked: sharedPool.blocked,
                                sharedPoolMessage: sharedPool.message
                            }
                        })}
                        activeId={selectedColorWay}
                        onSpecialSelect={() => handleConfiguratorChoice(true)}
                        onSelect={(id) => {
                            // If clicking the custom colors option when it's already selected
                            if (id === selectedColorWay) {
                                const selectedProduct = requiredOption?.options.find(opt => opt._id === id)
                                if (selectedProduct?.optionTitle?.toLowerCase().includes('custom')) {
                                    setIsInitialLoad(false)
                                    setCustomColoursOpen(true)
                                    return
                                }
                            }
                            // Otherwise handle normal selection
                            if (id) {
                                setSelectedColorWay(id)
                                queueScrollToNextRow(requiredOption.productCatalogueId)
                            }
                        }}
                        info={requiredOption?.info}
                        showInlineGroups={["Earth", "Dream", "Fruit", "Environment", "Custom"]}
                        onMoreButtonClick={(_items) => {
                            setMoreColoursData({ groupName: "All Colours", items: displayedColourWays });
                            setShowMoreColoursModal(true);
                        }}
                        primaryTextColor={primaryTextColor}
                        secondaryTextColor={secondaryTextColor}
                        priceColor={priceColor}
                        onBlockedSelect={onBlockedSelect}
                    />
                </div>
            )}

            {/* Other addons in order */}
            {options.map((option: IOption) => {
                if (option.required) {
                    return null
                }
                const addonItems: IAddonItem[] = option.options.map((product: IProduct) => {
                    const isEngravingOption = option.productCatalogueId === '67519d69197065b8a54a327e'
                    const engravingConfig = product.componentModifications?.engravingText
                    const isFree = isAddonFree(product._id, option.productCatalogueId)
                    const calculatedStock = stockById ? stockById[String(product._id)] : undefined
                    const sharedPoolStatusOverride = getSharedPoolStatusOverride(String(product._id))
                    const stockStatus = resolveStockStatus(
                        calculatedStock,
                        product.stockLimits?.stockStatus as StockStatus | undefined
                    )
                    const stockMessage = product.stockLimits?.stockMessage ?? undefined
                    const remainingStock = calculatedStock?.remainingStock ?? product.stockLimits?.remainingStock ?? null
                    const availableForSale = calculatedStock?.availableForSale ?? product.stockLimits?.availableForSale ?? undefined
                    const sharedPool = getSharedPoolBlock(String(product._id))

                    return {
                        id: product._id,
                        imageUrl: getProductThumbnailUrl(product) || getLegacyThumbnailPath(product._id),
                        title: product.optionTitle || '',
                        price: isFree ? 0 : (product.price?.unit_amount ?? 0), // Show 0 if free
                        currency: currencySymbol,
                        triggerInput: isEngravingOption,
                        inputPlaceholder: isEngravingOption ? 'Enter your engraving text' : undefined,
                        inputMaxLength: engravingConfig?.maxLength || 14,
                        inputPattern: '[A-Za-z0-9\\s.,!?\\-\'/]*',
                        inputClassName: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent font-body',
                        inputValidation: (value: string) => {
                            if (!value) return true
                            return /^[A-Za-z0-9\s.,!?\-'/]+$/.test(value)
                        },
                        isFree: isFree, // Add this property to show "FREE" badge
                        stockStatus: sharedPoolStatusOverride || stockStatus || 'IN_STOCK', // Add stock status
                        stockMessage: stockMessage, // Add custom stock message
                        remainingStock: remainingStock,
                        availableForSale: availableForSale,
                        sharedPoolBlocked: sharedPool.blocked,
                        sharedPoolMessage: sharedPool.message
                    }
                })

                // Check if this is engraving with free discount active
                const showFreeBadge = option.productCatalogueId === '67519d69197065b8a54a327e' &&
                    discountCode && discountCode.valid && discountCode.discountType === 'free_addon'

                return <div
                    key={option.productCatalogueId}
                    className="relative"
                    ref={(element) => { optionRowRefs.current[option.productCatalogueId] = element }}
                >
                    <Addon
                        title={option.title}
                        description={null}
                        items={addonItems}
                        activeId={getActiveId(option.productCatalogueId)}
                        onSelect={(id) => {
                            handleAddonSelect(option.productCatalogueId, id)
                            if (id) {
                                queueScrollToNextRow(option.productCatalogueId)
                            }
                        }}
                        onInputChange={inputChangeHandlers[option.productCatalogueId]}
                        info={option.info}
                        primaryTextColor={primaryTextColor}
                        secondaryTextColor={secondaryTextColor}
                        priceColor={priceColor}
                        onBlockedSelect={onBlockedSelect}
                        badge={showFreeBadge ? (
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{
                                    scale: [1, 1.1, 1],
                                    opacity: [1],
                                    y: [-1, -0.5, -1],
                                    rotate: [-5, 5, -5]
                                }}
                                transition={{
                                    opacity: { duration: 0.4, ease: "easeInOut" },
                                    scale: { duration: 1, repeat: Infinity, ease: "easeInOut" },
                                    y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                                    rotate: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                                }}
                            >
                                <FreeBadge className="text-specialYellow h-5" />
                            </motion.div>
                        ) : null}
                    />
                </div>
            })}
        </>
    )
}

export default ProductAddonSelector
